import type { AccountWithWsMembershipAvailability, MemberAvailability } from "@/store/types";
import {
  convertCamelCaseToCapitalize,
  convertSnakeCaseToNoCaseCapitalize,
  dateDiff,
  grayColor,
  greenColor,
  redColor,
  yellowColor,
} from "@/utils";
import { isWorkspaceMembershipOffline, offlineThreshold } from "./workspaceMembershipOffline";

export type TransformedWorkspaceMembershipAvailabilityRecord = {
  color: string;
  lastOnline?: string;
  drivingStatus?: string;
  dutyStatus?: string;
  availability: string;
  offline: boolean;
  status: string;
  toolTip: string;
  additionalInfo?: string[];
  volume: number;
};

export type ExtraStatusType = {
  value: string;
  active: boolean;
  statusType: boolean;
};

export const statusType = (availability: MemberAvailability): string => {
  switch (availability) {
    case "present":
      return "driving";
    case "unknown":
      return "not-driving";
    case "notPresent":
      return "not-present";
    default:
      return "unknown";
  }
};

export const transformWorkspaceMembershipAvailabilityRecord = (
  wsMaR: AccountWithWsMembershipAvailability,
): TransformedWorkspaceMembershipAvailabilityRecord => {
  /**
   * HOS [Hours of service]
   */
  if (!wsMaR || Object?.keys(wsMaR)?.length === 0) {
    return;
  }

  const handsFreeEnabled: boolean = wsMaR.mostRecentHandsFreeStatusEnabled === true;
  const muted: boolean = !(wsMaR.mostRecentVolumeAudible ?? true);
  const availability: string | null = wsMaR.calculatedAvailability;
  const dutyStatus: string | null = wsMaR.mostRecentDutyStatusStatus;
  const drivingStatus: string | null = wsMaR.mostRecentDrivingStatus
    ? wsMaR.mostRecentDrivingStatus === "active"
      ? "Driving"
      : "Not Driving"
    : null;

  // Guard against null/undefined timestamps producing epoch via new Date(null).
  // Availability records are purged after 2 days, so any field can be null.
  const toDateOrNull = (value: string | Date | null | undefined): Date | null => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const handsFreeUpdatedAt = toDateOrNull(wsMaR.mostRecentHandsFreeStatusUpdatedAt);
  const dutyStatusChange = toDateOrNull(wsMaR.mostRecentDutyStatusChangedAt);
  const drivingStatusChange = toDateOrNull(wsMaR.mostRecentDrivingStatusChangedAt);

  const offline = isWorkspaceMembershipOffline({
    handsFreeUpdatedAt: handsFreeUpdatedAt ?? undefined,
    offlineThreshold,
  });

  // The timestamp shown next to the status label depends on driver state:
  //   Offline  → handsFreeUpdatedAt (time since last heartbeat, i.e. "last seen X ago")
  //   Online with ELD (dutyStatus exists) → dutyStatusChangedAt (time in current duty status)
  //   Online without ELD → drivingStatusChangedAt (time in current driving state)
  // If the resolved timestamp is null (purged), no time is shown.
  const statusTimestamp = offline ? handsFreeUpdatedAt : dutyStatus ? dutyStatusChange : drivingStatusChange;

  const checkLastDateDiff = statusTimestamp ? dateDiff(statusTimestamp, 1) : null;
  const lastOnline: string = checkLastDateDiff?.lastOnline ?? "";

  // Determine the color of the status based on the availability
  let color = "";
  if (offline) {
    color = grayColor;
  } else {
    switch (availability) {
      case "present":
        color = greenColor;
        break;
      case "unknown":
        color = yellowColor;
        break;
      case "notPresent":
        color = redColor;
        break;
      default:
        color = grayColor;
    }
  }

  // Status text that displays in the header and the tooltip
  let combinedStatus = "";
  if (offline) {
    combinedStatus = "Offline";
  } else {
    switch (availability) {
      case "present":
        combinedStatus = "Driving";
        break;
      default: {
        combinedStatus = dutyStatus ?? drivingStatus;
        // if the availability is unknown, And dutyStatus is not present, And drivingStatus is not present set as Not Driving
        if (combinedStatus === null) {
          combinedStatus = "Not Driving";
        }
      }
    }
  }

  // Change the casing of values like offDuty to Off Duty
  const combinedStatusFixedCasing =
    combinedStatus?.length > 0 ? convertCamelCaseToCapitalize(convertSnakeCaseToNoCaseCapitalize(combinedStatus)) : "";

  const extras: ExtraStatusType[] = [
    {
      value: combinedStatusFixedCasing,
      active: combinedStatusFixedCasing?.length > 0,
      statusType: true,
    },
    {
      value: lastOnline,
      active: lastOnline?.length > 0,
      statusType: true,
    },
    {
      value: "Hands-Free Mode Off",
      active: !handsFreeEnabled,
      statusType: false,
    },
    {
      value: "Device Muted",
      active: muted,
      statusType: false,
    },
  ].filter((extra) => extra.active);

  // Combine all extra attributes into a single tooltip string
  const toolTip: string = extras?.map((extra) => extra.value)?.join(" · ");

  // Array of extras: Muted | Hands-Free Mode Off
  const additionalInfo: string[] = extras?.filter((aI) => !aI.statusType)?.map((aI) => aI.value);

  const roundedVolume =
    typeof wsMaR?.mostRecentVolumeVolume === "number" ? Math.round(wsMaR.mostRecentVolumeVolume * 100) : null;

  const status = `${combinedStatusFixedCasing} ${lastOnline}`?.trim().replace(/ {2}/g, "");

  return {
    color,
    lastOnline,
    dutyStatus,
    drivingStatus,
    availability,
    offline,
    toolTip,
    additionalInfo,
    status,
    volume: roundedVolume,
  };
};
